/**
 * Your main areas and the topics you know best. Two different things that are never merged.
 *
 * MAIN AREAS (`expertise`) come from the curated catalogue — the SAME one articles use — and are
 * always public: the row's `isPublic` is sent true and there is no switch for it (the owner,
 * 2026-10-19; migration 0118 made the rows saved private before then public). The catalogue is
 * fetched from `listEditorCategories`, never baked
 * in: the prototype bakes a category list twice and one of its copies contains both "Rituals" and
 * "Ritual", which is exactly what a hard-coded vocabulary does to you. The readers' apps filter the
 * authors page by them, which is the reason the field gives ("Readers find authors by these areas.").
 *
 * THAT OPERATION IS REACHABLE BY AN APPLICANT despite being named for the editor: it declares 200,
 * 401 and 503 and NO 403, so any signed-in Firebase user may load it.
 *
 * ★ AT MOST THREE MAIN AREAS, AND AT MOST EIGHT TOPICS (design 24, T25, the prototype's "Primary
 * topics" and "Secondary topics"). Both are cabinet limits (`EXPERTISE_MAX`, `TOPICS_MAX`,
 * ../domain/limits.ts): a new pick past them is stopped and the field says why; an author already
 * holding more keeps them, and nothing refuses the save.
 *
 * TOPICS (`tags`) are 2–40 characters each, with their own `isPublic` per row, and since T25 they
 * are PICKED from MoonX's own tag list - grouped, searchable (`suggestTags`' `groups`, design 24 T6)
 * - with a topic not on it still typed in. Until a server sends that list, the field offers what
 * other authors have used, which is what `suggestTags` always answered. They replaced "Your own
 * tags", the free box that read like the bio again.
 *
 * BOTH ARRAYS ARE SENT WHOLE. There is no per-item delete route anywhere on the surface, so
 * whole-set replacement is the only reading under which removing one is possible.
 */
import { Badge, Chip, Group, Stack, TagsInput, Text } from '@mantine/core';
import type { ComboboxData } from '@mantine/core';
import type { ReactNode } from 'react';
import { useTranslation } from '../../../i18n';
import type { AuthorTag, CategoryList, ExpertiseEntry, TagSuggestions } from '../contract';
import { useTagList } from '../data/useTagList';
import { EXPERTISE_MAX, LIMITS, TOPICS_MAX } from '../domain/limits';
import { InlineFailure, LoadingState } from './States';

export interface ExpertisePickerProps {
  readonly id: string;
  readonly value: readonly ExpertiseEntry[];
  readonly onChange: (next: readonly ExpertiseEntry[]) => void;
  readonly catalogue: CategoryList | undefined;
  readonly loading: boolean;
  readonly loadError: unknown;
  readonly onRetry: () => void;
  readonly disabled?: boolean;
  readonly applying?: boolean;
}

export function ExpertisePicker({
  id,
  value,
  onChange,
  catalogue,
  loading,
  loadError,
  onRetry,
  disabled,
  applying = true,
}: ExpertisePickerProps): ReactNode {
  const { t } = useTranslation('application');
  const selected = new Map(value.map((entry) => [entry.categoryCode, entry]));
  // A new pick stops at the limit; what is already held stays, and can always be let go.
  const full = value.length >= EXPERTISE_MAX;

  function toggle(code: string): void {
    if (selected.has(code)) onChange(value.filter((entry) => entry.categoryCode !== code));
    // The chip is disabled past the limit; this is the same rule, for any other way in.
    else if (full) return;
    // Always public: main areas have no switch (the owner, 2026-10-19).
    else onChange([...value, { categoryCode: code, isPublic: true }]);
  }

  return (
    <Stack gap="sm" id={id}>
      <Stack gap={2}>
        <Group gap="xs">
          <Text fw={700} size="sm">
            {t('expertise.title')}
          </Text>
          {applying ? <NeededBadge /> : null}
        </Group>
        <Text size="xs" c="dimmed">
          {applying
            ? t('expertise.hint.applying', { max: EXPERTISE_MAX })
            : t('expertise.hint.author', { max: EXPERTISE_MAX })}
        </Text>
      </Stack>

      {loading ? (
        <LoadingState label={t('expertise.loading')} lines={2} />
      ) : loadError !== undefined && loadError !== null ? (
        <InlineFailure error={loadError} fallback={t('expertise.loadFailed')} onRetry={onRetry} />
      ) : catalogue === undefined || catalogue.items.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t('expertise.none')}
        </Text>
      ) : (
        <>
          <Group gap="xs">
            {/* Already ordered by `sortOrder` on the wire, so it is rendered as sent. */}
            {catalogue.items.map((category) => (
              <Chip
                key={category.code}
                checked={selected.has(category.code)}
                disabled={disabled === true || (full && !selected.has(category.code))}
                onChange={() => {
                  toggle(category.code);
                }}
              >
                {category.label}
              </Chip>
            ))}
          </Group>

          {full ? (
            <Text size="xs" c="dimmed" role="status">
              {t('expertise.full', { count: EXPERTISE_MAX })}
            </Text>
          ) : null}
        </>
      )}
    </Stack>
  );
}

export interface TopicsPickerProps {
  readonly id: string;
  readonly value: readonly AuthorTag[];
  readonly onChange: (next: readonly AuthorTag[]) => void;
  readonly error: string | undefined;
  /** `suggestTags` asked with nothing typed; `undefined` while it loads or after it failed. */
  readonly suggestions: TagSuggestions | undefined;
  readonly disabled?: boolean;
}

/**
 * "Topics you know best" as drawn: a tags field whose list is MoonX's own, in its groups, or - from
 * a server with no list yet - the tags other authors use. Typing narrows the whole list; a topic
 * already added is not offered again (the field's own rule, case-blind); anything else can still be
 * typed in.
 */
export function TopicsPicker({
  id,
  value,
  onChange,
  error,
  suggestions,
  disabled,
}: TopicsPickerProps): ReactNode {
  const { t } = useTranslation('application');
  const listed = (suggestions?.groups ?? []).some((group) => group.tags.length > 0);
  const full = value.length >= TOPICS_MAX;
  return (
    <Stack gap={6} id={id}>
      <TagsInput
        label={t('topics.label')}
        description={t(listed ? 'topics.description.listed' : 'topics.description.unlisted', {
          max: TOPICS_MAX,
          min: LIMITS.tagText.min,
          maxLength: LIMITS.tagText.max,
        })}
        placeholder={full ? undefined : t('topics.placeholder')}
        data={topicOptions(suggestions)}
        maxTags={TOPICS_MAX}
        maxDropdownHeight={280}
        value={value.map((tag) => tag.tagText)}
        disabled={disabled === true}
        error={error ?? null}
        onChange={(next) => {
          // Keep each row's own `isPublic` across an edit; a new one is public, the row's default.
          const existing = new Map(value.map((tag) => [tag.tagText, tag]));
          onChange(next.map((text) => existing.get(text) ?? { tagText: text, isPublic: true }));
        }}
      />
      {full ? (
        <Text size="xs" c="dimmed" role="status">
          {t('topics.full', { count: TOPICS_MAX })}
        </Text>
      ) : null}
    </Stack>
  );
}

export type TopicsFieldProps = Omit<TopicsPickerProps, 'suggestions'>;

/** The page's field: the picker, with the list read beside it (../data/useTagList.ts). */
export function TopicsField(props: TopicsFieldProps): ReactNode {
  const list = useTagList();
  return <TopicsPicker {...props} suggestions={list.data} />;
}

/**
 * The list as the field offers it: MoonX's groups in their order, or other authors' tags when there
 * are none. Each topic once across the whole list (an option's value must be unique), case-blind.
 */
function topicOptions(suggestions: TagSuggestions | undefined): ComboboxData {
  const seen = new Set<string>();
  const once = (tags: readonly string[]): string[] =>
    tags.filter((tag) => {
      const key = tag.trim().toLowerCase();
      if (key === '' || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  const groups = (suggestions?.groups ?? [])
    .map((group) => ({ group: group.label, items: once(group.tags) }))
    .filter((group) => group.items.length > 0);
  if (groups.length > 0) return groups;
  return once((suggestions?.items ?? []).map((tag) => tag.tagText));
}

/**
 * ★ "REQUIRED" AND "PICK AT LEAST ONE" ARE AN APPLICATION'S WORDS - `applying` on the picker. A
 * subject is one of the four rows that gate sending an application; an approved author's profile
 * gates nothing (design 14, F3), and the owner's live walk of real cabinets (2026-10-01) still
 * found the badge on their profiles. So `applying` false (`rowsGateSending`, from the screen) drops
 * both and keeps the reason a subject is worth choosing. Written at the foot so that the lines
 * above, cited by number, keep theirs.
 */
function NeededBadge(): ReactNode {
  const { t } = useTranslation('application');
  return (
    <Badge size="xs" variant="default">
      {t('expertise.required')}
    </Badge>
  );
}
